/**
 * Timed attack delivery shared by Art Lab Composite and combat presentation.
 * Origin at muzzle; stages delayed by impactFrac / attackTravelSec.
 */
import type Phaser from "phaser";
import {
  attackTravelSec,
  resolveHitPreset,
  type AttackFxRoleConfig,
} from "./attack-fx";
import { playSpriteFx } from "./sprite-fx-player";
import type { VfxPlayer } from "./vfx-player";

export type TimedAttackFxOpts = {
  scene: Phaser.Scene;
  vfx: VfxPlayer;
  fx: AttackFxRoleConfig;
  /** Affiliation for sprite FX rim bake. */
  affiliation: string;
  /** Fallback pack when fx.spriteFxPackId missing. */
  packId: string;
  originX: number;
  originY: number;
  impactX: number;
  impactY: number;
  angle: number;
  /** Used for travel timing (arc/pulse fraction of swing). */
  swingSec: number;
  /** Pixel → cell scale for bolt travel (default 64). */
  cellPx?: number;
  /** Override catalog travel (seconds). */
  travelOverrideSec?: number;
  depth?: number;
  floatDepth?: number;
  hitTargets?: Record<string, Phaser.GameObjects.GameObject | undefined>;
  damage?: number;
  /** Merged into floatText vars (e.g. faction `color` override). */
  floatVars?: Record<string, string | number>;
  /** Victim kind for takenHit vs hit preset. */
  targetKind?: "ally" | "enemy" | "structure" | "barrier";
  /**
   * Bolt only: feature owns projectile trail and must call `playHit` at impact.
   * When set, built-in delayed hit is skipped.
   */
  onBoltFlight?: (ctx: {
    travelMs: number;
    playHit: () => void;
  }) => void;
  /** Extra callback when hit fires. */
  onHit?: () => void;
  /** Skip default flight particle (caller draws travel). */
  skipDefaultFlight?: boolean;
  /** Skip built-in delayed hit (melee: Hit event owns impact). */
  skipDefaultHit?: boolean;
};

/**
 * Play sprite FX + particle origin/flight/hit with combat-like timing.
 * Returns travel duration in seconds (for callers syncing attack anim).
 */
export function playTimedAttackFx(opts: TimedAttackFxOpts): number {
  const {
    scene,
    vfx,
    fx,
    affiliation,
    packId,
    originX,
    originY,
    impactX,
    impactY,
    angle,
    swingSec,
    cellPx = 64,
    travelOverrideSec,
    depth = 8,
    floatDepth = 12,
    hitTargets,
    damage = 12,
    floatVars,
    targetKind = "enemy",
    onBoltFlight,
    onHit,
    skipDefaultFlight = false,
    skipDefaultHit = false,
  } = opts;

  const distCells =
    Math.hypot(impactX - originX, impactY - originY) / Math.max(1, cellPx);
  const travelSec =
    travelOverrideSec ?? attackTravelSec(fx, distCells, swingSec);
  const travelMs = Math.round(travelSec * 1000);
  const layer = { depth, floatDepth };

  const spriteAtImpact = fx.spriteFxAt === "impact";
  if (fx.spriteFxId) {
    const playSprite = () =>
      playSpriteFx(scene, {
        packId: fx.spriteFxPackId ?? packId,
        spriteId: fx.spriteFxId!,
        affiliation,
        x: spriteAtImpact ? impactX : originX,
        y: spriteAtImpact ? impactY : originY,
        angle,
        depth,
      });
    if (spriteAtImpact) {
      scene.time.delayedCall(travelMs, playSprite);
    } else {
      playSprite();
    }
  }

  vfx.play(fx.originPreset, {
    x: originX,
    y: originY,
    angle,
    ...layer,
  });

  if (!skipDefaultFlight) {
    if (fx.delivery === "bolt") {
      vfx.play(fx.flightPreset, {
        x: originX,
        y: originY,
        angle,
        ...layer,
      });
    } else if (fx.delivery === "pulse") {
      vfx.play(fx.flightPreset, {
        x: (originX + impactX) * 0.5,
        y: (originY + impactY) * 0.5,
        angle,
        ...layer,
      });
    } else {
      const midMs = Math.round(travelSec * 500);
      scene.time.delayedCall(midMs, () => {
        vfx.play(fx.flightPreset, {
          x: (originX + impactX) * 0.5,
          y: (originY + impactY) * 0.5,
          angle,
          ...layer,
        });
      });
    }
  }

  const fireHit = () => {
    if (!skipDefaultHit) {
      const hitId = resolveHitPreset(fx, targetKind);
      vfx.play(hitId, {
        x: impactX,
        y: impactY,
        angle,
        targets: hitTargets,
        vars: { damage, ...floatVars },
        ...layer,
      });
    }
    onHit?.();
  };

  if (fx.delivery === "bolt" && onBoltFlight) {
    onBoltFlight({ travelMs, playHit: fireHit });
    return travelSec;
  }

  if (!skipDefaultHit) {
    scene.time.delayedCall(travelMs, fireHit);
  } else {
    onHit?.();
  }
  return travelSec;
}
